import { Fragment } from 'react'
import { useTheme } from '@emotion/react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import Sticky from 'react-stickynode'

import { termsAndPrivacyIndexes } from '@/modules/termsAndPrivacy/constants'
import { Link as ScrollLink, ScrollOption } from '@/lib/link'

interface Props {
  scrollProps?: ScrollOption
  bottomBoundary?: string | number
}

function Sidebar(props: Props): React.ReactElement {
  const { bottomBoundary, scrollProps } = props

  const { pathname } = useRouter()
  const theme = useTheme()

  return (
    <aside
      css={theme.mq({
        display: {
          base: 'none',
          md: 'grid',
        },
        alignContent: 'start',
        gap: '16px',
        top: '224px',
        padding: '32px 18px 0 0',
      })}
    >
      <Sticky top={111} bottomBoundary={bottomBoundary}>
        {Object.values(termsAndPrivacyIndexes).map((menuItem, idx) => {
          const regex = new RegExp(`${menuItem.linkTo}`)
          const isActive = regex.test(pathname)

          return (
            <Fragment key={idx}>
              <Link href={menuItem.linkTo} passHref>
                <h2
                  css={theme.mq({
                    fontWeight: 'medium',
                    color: isActive
                      ? theme.colors.primary[400]
                      : theme.colors.gray[900],
                    cursor: 'pointer',
                    marginBottom: '12px',
                  })}
                >
                  {menuItem.title}
                </h2>
              </Link>

              {isActive && menuItem.subMenus && (
                <ul
                  css={theme.mq({
                    display: 'grid',
                    gridAutoRows: 'minmax(24px, max-content)',
                    gap: '18px',
                    alignItems: 'center',
                    marginBottom: '60px',
                  })}
                >
                  {menuItem.subMenus.map((navi, idx) => (
                    <li
                      key={idx}
                      css={theme.mq({
                        paddingLeft: navi.isSub ? '40px' : '20px',
                        display: 'flex',
                      })}
                    >
                      <ScrollLink
                        href={{
                          pathname: pathname,
                          hash: navi.scrollTo,
                        }}
                        scrollOption={{
                          hashSpy: false,
                        }}
                        css={theme.mq({
                          cursor: 'pointer',
                          transition: 'color 0.2s, font-weight 0.2s',
                          position: 'relative',
                          color: 'gray.900',
                          fontWeight: 'light',
                          whiteSpace: 'normal',

                          '&::before': {
                            display: 'block',
                            position: 'absolute',
                            width: '6px',
                            height: '32px',
                            borderRadius: '4px',
                            backgroundColor: 'primary.400',
                            left: '-22px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                          },

                          '&:active': {
                            color: 'primary.400',
                            fontWeight: 'medium',

                            '&::before': {
                              content: '""',
                            },
                          },

                          '&:hover': { color: 'primary.300' },
                        })}
                        {...scrollProps}
                      >
                        {navi.title}
                      </ScrollLink>
                    </li>
                  ))}
                </ul>
              )}
            </Fragment>
          )
        })}
      </Sticky>
    </aside>
  )
}

export default Sidebar
